import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { isNotNullOrUndefined } from '@ep/shared';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { FA_ICON } from '@shared/icons';
import { ListPaginationService } from '@app/resources/services/list-pagination.service';

enum BREADCRUMB_HOME_TYPES {
  ADMIN = 'Admin',
}

const BREADCRUMB_HOME_OBJECTS: { [index: string]: MenuItem } = {
  [BREADCRUMB_HOME_TYPES.ADMIN]: {
    label: BREADCRUMB_HOME_TYPES.ADMIN,
  },
};

interface BreadcrumbArea {
  listLabel: string;
  detailLabel: string;
  icon?: string;
  entityLabels?: { [entityId: string]: string };
}

const BREADCRUMB_AREAS: { [area: string]: BreadcrumbArea } = {
  members: { listLabel: 'Members', detailLabel: 'Member Details', icon: FA_ICON.USERS },
  'non-members': { listLabel: 'Non Members', detailLabel: 'Non Member Details', icon: 'bi bi-person-x' },
  transactions: { listLabel: 'Transactions', detailLabel: 'Order Details', icon: FA_ICON.CREDIT_CARD },
  'admin/devices': {
    listLabel: 'Devices',
    detailLabel: 'Existing Device',
    icon: FA_ICON.CALCULATOR,
    entityLabels: { new: 'New Device' },
  },
  'admin/employees': {
    listLabel: 'Employees',
    detailLabel: 'Existing Employee',
    icon: FA_ICON.EMPLOYEES,
    entityLabels: { new: 'New Employee' },
  },
  'admin/questions': {
    listLabel: 'Questions',
    detailLabel: 'Existing Question',
    icon: FA_ICON.QUESTIONS,
    entityLabels: { new: 'New Question' },
  },
};

const AREA_MAX_SEGMENTS = Math.max(...Object.keys(BREADCRUMB_AREAS).map((area) => area.split('/').length));

interface BreadcrumbPage {
  area: string;
  entityId?: string;
}

@Injectable({
  providedIn: 'root',
})
export class BreadcrumbService {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly listPagination = inject(ListPaginationService);

  static readonly ROUTE_DATA_BREADCRUMB = 'breadcrumb';
  static readonly ROUTE_DATA_ICON = 'icon';
  static readonly BREADCRUMB_HOME_ICON = 'pi pi-home';
  static readonly DEFAULT_BREADCRUMB_HOME = { icon: BreadcrumbService.BREADCRUMB_HOME_ICON };
  static readonly TRAIL_QUERY_PARAM = 'from';

  private pageBreadcrumbsPath: string | null = null;

  private _breadcrumbs$: BehaviorSubject<MenuItem[]> = new BehaviorSubject<MenuItem[]>([]);
  private _home$: BehaviorSubject<MenuItem> = new BehaviorSubject<MenuItem>({ icon: 'pi pi-home' });

  readonly breadcrumbs$: Observable<MenuItem[]> = this._breadcrumbs$.asObservable();
  readonly home$: Observable<MenuItem> = this._home$.asObservable().pipe(filter(isNotNullOrUndefined));

  get shouldShowBreadcrumbs() {
    return this._breadcrumbs$.value.length > 1;
  }

  setBreadcrumbs(breadcrumbs: MenuItem[]) {
    this._breadcrumbs$.next(breadcrumbs);
  }

  setHomeLabelAndUrl(data: MenuItem) {
    this._home$.next({
      icon: BreadcrumbService.BREADCRUMB_HOME_ICON,
      ...data,
    });
  }

  updateBreadcrumbs(locationId: number) {
    if (this.router.url.includes('admin'))
      this.setHomeLabelAndUrl({
        ...BREADCRUMB_HOME_OBJECTS[BREADCRUMB_HOME_TYPES.ADMIN],
        command: () => {
          this.router.navigate([`${locationId}/admin`]);
        },
      });
    else this.setHomeLabelAndUrl(BreadcrumbService.DEFAULT_BREADCRUMB_HOME);

    if (this.pageBreadcrumbsPath === this.currentPath()) return;
    this.pageBreadcrumbsPath = null;

    const breadcrumbs = this.createAreaBreadcrumbs(locationId) ?? this.createBreadcrumbs(this.route);
    this.setBreadcrumbs(this.disableCurrentPageCommand(breadcrumbs));
  }

  setPageBreadcrumbs(breadcrumbs: MenuItem[]) {
    const path = this.currentPath();

    Promise.resolve().then(() => {
      if (path !== this.currentPath()) return;

      this.pageBreadcrumbsPath = path;
      this.setBreadcrumbs(this.disableCurrentPageCommand(breadcrumbs));
    });
  }

  buildTrailQueryParams(): Params {
    const currentPage = this.parseCurrentPage();
    if (currentPage === null) return {};

    const trail = { [BreadcrumbService.TRAIL_QUERY_PARAM]: this.buildPagePath(currentPage) };
    const isSameArea = this.parseListStateArea() === currentPage.area;

    return isSameArea ? { ...trail, ...this.listPagination.queryParams() } : trail;
  }

  listQueryParamsByArea(): { [area: string]: Params } {
    const listArea = this.parseListStateArea();
    const listState = this.listPagination.queryParams();

    return Object.keys(BREADCRUMB_AREAS).reduce(
      (params: { [area: string]: Params }, area) => ({ ...params, [area]: area === listArea ? listState : {} }),
      {}
    );
  }

  private createAreaBreadcrumbs(locationId: number): MenuItem[] | null {
    const currentPage = this.parseCurrentPage();
    if (currentPage === null) return null;

    const originPage = isNotNullOrUndefined(currentPage.entityId) ? this.parseOriginPage() : null;

    return originPage === null
      ? this.createPageBreadcrumbs(locationId, currentPage)
      : [...this.createPageBreadcrumbs(locationId, originPage), this.createBreadcrumb(locationId, currentPage)];
  }

  private parseListStateArea(): string | null {
    const currentPage = this.parseCurrentPage();
    if (currentPage === null) return null;

    const originPage = isNotNullOrUndefined(currentPage.entityId) ? this.parseOriginPage() : null;

    return (originPage ?? currentPage).area;
  }

  private createPageBreadcrumbs(locationId: number, page: BreadcrumbPage): MenuItem[] {
    const listState = this.listPagination.queryParams();

    const listBreadcrumb: MenuItem = {
      ...this.createBreadcrumb(locationId, { area: page.area }, listState),
      icon: BREADCRUMB_AREAS[page.area].icon,
    };

    return isNotNullOrUndefined(page.entityId)
      ? [
          listBreadcrumb,
          this.createBreadcrumb(locationId, page, {
            [BreadcrumbService.TRAIL_QUERY_PARAM]: page.area,
            ...listState,
          }),
        ]
      : [listBreadcrumb];
  }

  private createBreadcrumb(locationId: number, page: BreadcrumbPage, queryParams: Params = {}): MenuItem {
    const area = BREADCRUMB_AREAS[page.area];
    const url = `/${locationId}/${this.buildPagePath(page)}`;

    return {
      label: isNotNullOrUndefined(page.entityId)
        ? area.entityLabels?.[page.entityId] ?? area.detailLabel
        : area.listLabel,
      command: () => {
        this.router.navigate([url], { queryParams });
      },
    };
  }

  private disableCurrentPageCommand(breadcrumbs: MenuItem[]): MenuItem[] {
    return breadcrumbs.map((breadcrumb, index) =>
      index === breadcrumbs.length - 1 ? { ...breadcrumb, command: undefined } : breadcrumb
    );
  }

  private buildPagePath(page: BreadcrumbPage): string {
    return isNotNullOrUndefined(page.entityId) ? `${page.area}/${page.entityId}` : page.area;
  }

  private parseCurrentPage(): BreadcrumbPage | null {
    const [locationId, ...pageSegments] = this.splitPath(this.router.url);

    return isNotNullOrUndefined(locationId) ? this.parsePage(pageSegments) : null;
  }

  private parseOriginPage(): BreadcrumbPage | null {
    const trail = this.router.parseUrl(this.router.url).queryParamMap.get(BreadcrumbService.TRAIL_QUERY_PARAM);

    return isNotNullOrUndefined(trail) ? this.parsePage(this.splitPath(trail)) : null;
  }

  private parsePage(segments: string[]): BreadcrumbPage | null {
    for (let length = AREA_MAX_SEGMENTS; length > 0; length--) {
      const area = segments.slice(0, length).join('/');

      if (isNotNullOrUndefined(BREADCRUMB_AREAS[area])) return { area, entityId: segments[length] };
    }

    return null;
  }

  private currentPath(): string {
    return this.splitPath(this.router.url).join('/');
  }

  private splitPath(url: string): string[] {
    const [path] = url.split(/[?#]/);

    return path.split('/').filter((segment) => segment !== '');
  }

  private createBreadcrumbs(route: ActivatedRoute, url: string = '', breadcrumbs: MenuItem[] = []): MenuItem[] {
    const children: ActivatedRoute[] = route.children;

    if (children.length === 0) {
      return breadcrumbs;
    }

    for (const child of children) {
      const routeURL: string = child.snapshot.url.map((segment) => segment.path).join('/');
      if (routeURL !== '') {
        url += `/${routeURL}`;
      }

      const label = child.snapshot.data[BreadcrumbService.ROUTE_DATA_BREADCRUMB];
      const icon = child.snapshot.data[BreadcrumbService.ROUTE_DATA_ICON];
      if (isNotNullOrUndefined(label)) {
        breadcrumbs.push({
          label,
          command: () => {
            this.router.navigate([url]);
          },
          icon,
        });
      }

      return this.createBreadcrumbs(child, url, breadcrumbs);
    }

    return breadcrumbs;
  }
}
